import {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
} from "react";

const ThumbnailContext = createContext();

export const ThumbnailProvider = ({ children }) => {
    const [thumbnailCache, setThumbnailCache] = useState({});
    const pendingUpdatesRef = useRef({});
    const batchTimeoutRef = useRef(null);

    // Batch update function - runs in background after all thumbnails are generated
    const flushBatchUpdate = useCallback((designId) => {
        if (pendingUpdatesRef.current[designId]) {
            setThumbnailCache((prev) => ({
                ...prev,
                [designId]: {
                    thumbnails: pendingUpdatesRef.current[designId],
                    timestamp: Date.now(),
                    isComplete: true,
                },
            }));

            // Clear pending updates
            delete pendingUpdatesRef.current[designId];
        }
    }, []);

    // Stage thumbnails for batch update (non-blocking)
    const stageThumbnailsForCache = useCallback(
        (designId, thumbnails) => {
            pendingUpdatesRef.current[designId] = thumbnails;

            // Clear existing timeout
            if (batchTimeoutRef.current?.[designId]) {
                clearTimeout(batchTimeoutRef.current[designId]);
            }

            // Schedule background update using requestIdleCallback or setTimeout
            if (typeof requestIdleCallback !== "undefined") {
                requestIdleCallback(() => flushBatchUpdate(designId), {
                    timeout: 2000,
                });
            } else {
                // Fallback to setTimeout for background processing
                batchTimeoutRef.current = {
                    ...batchTimeoutRef.current,
                    [designId]: setTimeout(
                        () => flushBatchUpdate(designId),
                        100
                    ),
                };
            }
        },
        [flushBatchUpdate]
    );

    // Immediate batch update (when all thumbnails are ready)
    const cacheThumbnails = useCallback((designId, thumbnails) => {
        setThumbnailCache((prev) => ({
            ...prev,
            [designId]: {
                thumbnails,
                timestamp: Date.now(),
                isComplete: true,
            },
        }));
    }, []);

    // Get cached thumbnails
    const getCachedThumbnails = useCallback(
        (designId, maxAge = 300000) => {
            const cached = thumbnailCache[designId];

            if (!cached) return null;

            // Check if cache is still valid
            if (Date.now() - cached.timestamp > maxAge) {
                return null;
            }

            return cached.thumbnails;
        },
        [thumbnailCache]
    );

    // Update single thumbnail (for edit page updates)
    const updateSingleThumbnail = useCallback(
        (designId, indexSr, thumbnail) => {
            setThumbnailCache((prev) => {
                const cached = prev[designId];

                if (!cached) return prev;

                const updatedThumbnails = [...cached.thumbnails];
                const index = updatedThumbnails.findIndex(
                    (t) => parseInt(t.indexSr) === parseInt(indexSr)
                );

                if (index !== -1) {
                    updatedThumbnails[index] = thumbnail;
                } else {
                    updatedThumbnails.push(thumbnail);
                }

                return {
                    ...prev,
                    [designId]: {
                        ...cached,
                        thumbnails: updatedThumbnails,
                        timestamp: Date.now(),
                    },
                };
            });
        },
        []
    );

    // Clear cache for specific design
    const clearCache = useCallback((designId) => {
        setThumbnailCache((prev) => {
            const newCache = { ...prev };
            delete newCache[designId];
            return newCache;
        });

        // Clear any pending updates
        delete pendingUpdatesRef.current[designId];
    }, []);

    const value = {
        getCachedThumbnails,
        cacheThumbnails,
        stageThumbnailsForCache,
        updateSingleThumbnail,
        clearCache,
    };

    return (
        <ThumbnailContext.Provider value={value}>
            {children}
        </ThumbnailContext.Provider>
    );
};

export const useThumbnailCache = () => {
    const context = useContext(ThumbnailContext);
    if (!context) {
        throw new Error(
            "useThumbnailCache must be used within ThumbnailProvider"
        );
    }
    return context;
};
